/** LIVE pilot guard 상태 패널. */
import { LivePilotGuards, RuntimeRiskProfile } from '../../services/operationsApi';

type GuardStatus = 'standby' | 'pass' | 'fail' | 'unknown';

const LABEL: Record<GuardStatus, string> = {
  standby: 'STANDBY',
  pass: 'PASS',
  fail: 'FAIL',
  unknown: 'CHECK',
};

export default function OperationsLivePilotGuards({ status }: { status?: LivePilotGuards }) {
  if (!status) return null;
  const profile = status.standby_profile || status.profile;
  const { active_profile, guards } = status;
  const sectorText = Object.entries(guards.sector_limit.exposure || {})
    .map(([sector, count]) => `${sector} ${count}`)
    .join(' · ') || '-';
  const recentLoss = guards.cooldown.recent_loss_symbols.join(', ') || '-';

  const rows = [
    {
      key: 'daily',
      title: 'Daily Loss',
      status: guards.daily_loss.status,
      value: pct(guards.daily_loss.daily_return_percent),
      sub: `limit ${profile.daily_loss_limit_percent}%`,
    },
    {
      key: 'sector',
      title: 'Sector Limit',
      status: guards.sector_limit.status,
      value: `max ${profile.max_positions_per_sector}`,
      sub: sectorText,
    },
    {
      key: 'cooldown',
      title: 'Cooldown',
      status: guards.cooldown.status,
      value: `${profile.loss_cooldown_days}d`,
      sub: recentLoss,
    },
  ];

  return (
    <section className="op-live-guards-panel">
      <div className="op-live-guards-head">
        <div>
          <h3>운용 프로필 / LIVE pilot guards</h3>
          <p>현재 주문 경로에 적용 중인 profile과 live_pilot guard 상태를 표시합니다.</p>
        </div>
        <div className="op-live-badges">
          <span className={`op-live-mode ${status.active ? 'active' : 'standby'}`}>
            {status.mode}
          </span>
          <span className={`op-live-mode ${status.live_trading_enabled ? 'active' : 'standby'}`}>
            live orders {status.live_orders}
          </span>
        </div>
      </div>

      <div className="op-profile-split">
        <article className="op-profile-card active">
          <span>현재 운용 프로필</span>
          <strong>{active_profile.name}</strong>
          <p>{profileSizing(active_profile)}</p>
          <small>
            max {fmt(active_profile.max_positions)} positions · day {fmt(active_profile.max_trades_per_day)} trades ·
            buy≥{pctPlain(active_profile.buy_threshold)} · sell≤{pctPlain(active_profile.sell_threshold)}
          </small>
        </article>
        <article className={`op-profile-card ${profile.currently_applied ? 'active' : 'standby'}`}>
          <span>live_pilot 기준</span>
          <strong>{profile.name}</strong>
          <p>{profile.currently_applied ? '현재 적용 중' : '대기 중 - 주문 switch 잠금'}</p>
          <small>
            1회 주문 {profile.position_size_percent}% · 최대 {profile.max_positions}종목 ·
            일 {profile.max_trades_per_day}회 · 매수≥{pctPlain(profile.buy_threshold)}
          </small>
        </article>
      </div>

      <div className="op-live-guards-grid">
        {rows.map((row) => (
          <article key={row.key} className={`op-live-guard op-live-${row.status}`}>
            <div>
              <span>{row.title}</span>
              <strong>{row.value}</strong>
            </div>
            <p>{row.sub}</p>
            <small>{LABEL[row.status]}</small>
          </article>
        ))}
      </div>
    </section>
  );
}

function pct(value?: number | null): string {
  if (value == null) return '-';
  return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
}

function pctPlain(value?: number | null): string {
  if (value == null) return '-';
  return `${(value * 100).toFixed(0)}%`;
}

function fmt(value?: number | null): string {
  return value == null ? '-' : String(value);
}

function profileSizing(profile: RuntimeRiskProfile): string {
  if (profile.use_dynamic_position_size) {
    return `동적 사이징 ${fmt(profile.min_position_percent)}-${fmt(profile.max_position_percent)}%`;
  }
  return `정적 사이징 ${fmt(profile.position_size_percent)}%`;
}
